import { DateRange } from 'react-day-picker';
import { apiClient } from './axios';
import { ICustomer } from './customers';
import { IEmployee } from './employee';
export enum OrderStatus {
  NEW = 'new',
  PENDING = 'pending',
  ADMIN_APPROVED = 'admin_approved',
  GENERAL_DIRECTOR_APPROVED = 'general_director_approved',
  BUSINESS_APPROVED = 'business_approved',
  ACCOUNTING_APPROVED = 'accounting_approved',
  BUSINESS_REJECTED = 'business_rejected',
  ACCOUNTING_REJECTED = 'accounting_rejected',
  ADMIN_REJECTED = 'admin_rejected',
  GENERAL_DIRECTOR_REJECTED = 'general_director_rejected',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export interface IItemsOrder {
  id: string;
  productName: string;
  price: string;
  quantity: number;
  serialNumber?: string;
  description?: string;
}

export interface IOrder {
  id: string;
  customer: ICustomer;
  employee: IEmployee;
  budgetMaxBuyGift: number;
  color: string;
  items: IItemsOrder[];
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  createdBy: IEmployee;
  sellDate: string;
  reasonReject?: string;
  explanation?: string;
  amountToBePaid: number;
}

export interface OrderResponse {
  items: IOrder[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

export interface OrderFormData {
  customerId: number;
  employeeId: number;
  items: {
    productName: string;
    price: string;
    quantity: number;
    serialNumber?: string;
    description?: string;
    id?: string;
  }[];
  amountToBePaid?: number;
  sellDate: string;
  explanation?: string;
  budgetMaxBuyGift?: number;
}
export const getOrders = async (
  page: number,
  limit: number,
  search: string,
  customerId: string | null,
  employeeId: string | null,
  priceSearch: string | null,
  dateRange: DateRange | undefined,
  statusSearch: string | null
): Promise<OrderResponse> => {
  const response = await apiClient.get<OrderResponse>('orders', {
    params: {
      page,
      limit,
      productName: search,
      customerId,
      employeeId,
      priceSearch,
      dateRangeFrom: dateRange?.from?.toISOString(),
      dateRangeTo: dateRange?.to?.toISOString(),
      status: statusSearch,
    },
  });
  return response.data;
};

export const createOrder = async (order: OrderFormData): Promise<IOrder> => {
  const response = await apiClient.post<IOrder>('orders', order);
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export const updateOrder = async (
  id: string,
  order: OrderFormData
): Promise<IOrder> => {
  const response = await apiClient.patch<IOrder>(`orders/${id}`, order);
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export const deleteOrder = async (id: string): Promise<void> => {
  const response = await apiClient.delete(`orders/${id}`);
  if (!response.success && response?.error) {
    throw new Error(response.message);
  }
};

export const approveOrder = async (
  id: string,
  status: string,
  reasonReject?: string
): Promise<IOrder> => {
  const response = await apiClient.patch<IOrder>(`orders/${id}/status`, {
    status: status,
    reasonReject: reasonReject,
  });
  if (!response.success) {
    throw new Error(response.message);
  }
  return response.data;
};

export const getOrderByUser = async (): Promise<IOrder[]> => {
  const response = await apiClient.get<IOrder[]>(`orders/order-by-user`);
  return response.data;
};

export const sendToManagerMarketing = async (id: string): Promise<void> => {
  await apiClient.post(`orders/${id}/send-to-manager-marketing`);
};

// Order Statistics Interface
export interface IOrderMonthlyData {
  month: number;
  monthName: string;
  totalOrders: number;
  totalAmount: number;
  averageAmount: number;
}

export interface IOrderStatistics {
  year: number;
  employeeId: number;
  employeeName: string;
  monthlyData: IOrderMonthlyData[];
  totalOrders: number;
  totalAmount: number;
  averageAmount: number;
}

export interface ICustomerOrderStatistics {
  year: number;
  customerId: number;
  customerName: string;
  monthlyData: IOrderMonthlyData[];
  totalOrders: number;
  totalAmount: number;
  averageAmount: number;
}

export const getOrderStatistics = async (
  employeeId: number,
  year: number
): Promise<IOrderStatistics> => {
  const response = await apiClient.get<IOrderStatistics>(
    `orders/statistics/employee/${employeeId}/monthly/${year}`
  );
  return response.data;
};

export const getCustomerOrderStatistics = async (
  customerId: number,
  year: number
): Promise<ICustomerOrderStatistics> => {
  const response = await apiClient.get<ICustomerOrderStatistics>(
    `orders/statistics/customer/${customerId}/monthly/${year}`
  );
  return response.data;
};

// Total Order Statistics Interface
export interface ITotalOrderStatistics {
  year: number;
  monthlyData: IOrderMonthlyData[];
  totalOrders: number;
  totalAmount: number;
  averageAmount: number;
}

export const getTotalOrderStatistics = async (
  year: number
): Promise<ITotalOrderStatistics> => {
  const response = await apiClient.get<ITotalOrderStatistics>(
    `orders/statistics/total/yearly/${year}`
  );
  return response.data;
};
